<template>
  <div>
    <v-row align="center" no-gutters>
      <v-col cols="9">
        <ChargeListFilters />
      </v-col>
      <v-col cols="3" class="text-right">
        <v-btn :disabled="isGeneratingReport" @click="fetchChargeReport" class="mb-4" color="primary" outlined>
          <v-progress-circular
              v-if="isGeneratingReport"
              :size="14"
              :width="2"
              color="grey"
              class="mr-2"
              indeterminate
          />
          <v-icon v-else left>mdi-download-box-outline</v-icon>
          <span class="body-2">
            {{ $t('charge.label.generateReport') }}
          </span>
        </v-btn>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        v-model="selectedCharges"
        :items="charges"
        :headers="headers"
        :loading="isLoading"
        :items-per-page="itemsPerPage"
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
        class="hideSelectAll"
        mobile-breakpoint="20"
        hide-default-footer
        disable-sort
        show-select
      >
      <template v-slot:item.data-table-select="{ item, isSelected, select }">
        <v-simple-checkbox
          @input="select($event)"
          :disabled="item.installments.length >= 1"
          :value="isSelected"
          class="pa-0"
          hide-details
          dense
        />
      </template>
        <template v-slot:item.code="{ item }">
          <span class="font-weight-bold">
            {{ item.code }}
          </span>
        </template>
        <template v-slot:item.appointment="{ item }">
          <span
            v-if="item.appointment"
            @click="toAppointmentDetails(item.appointment.id)"
            class="font-weight-medium primary--text clickable"
          >
            {{ item.appointment.code }}
            <v-icon small color="primary">
              mdi-open-in-new
            </v-icon>
          </span>
          <span v-else>
            ---
          </span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="handleColors(handleChargeStatus(item.installments))"
            small
          >
            {{ $t(`recharge.status.${handleChargeStatus(item.installments)}`) }}
          </v-chip>
        </template>
        <template v-slot:item.type="{ item }">
          <div v-if="item.type === 'FCI'" class="d-flex align-center">
            F.C.I.
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  color="grey lighten-1"
                  small
                  right
                >
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span>{{ $t('appointment.formCreate.hints.fci') }}</span>
            </v-tooltip>
          </div>
          <div v-if="item.type === 'EVICTION'">
            {{ $t('appointment.type.EVICTION') }}
          </div>
        </template>
        <template v-slot:item.value="{ item }">
          {{ item.value ? formatMoney(item.value) : '---' }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="handleShowPayButton(handleChargeStatus(item.installments))"
            @click="handlePayment(item)"
            color="primary"
            outlined
            small
          >
            <v-icon left>mdi-cash</v-icon>
            <span class="body-2">
              {{ $t('charge.label.pay') }}
            </span>
          </v-btn>
          <v-icon v-else color="green" small> mdi-check </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <div class="d-flex justify-end text-right">
      <div :class="`totalToPay d-flex pa-3 white rounded ${selectedCharges.length ? 'show' : ''}`">
        <p class="mb-0 mr-5">
          <span class="d-block caption grey--text mb-n1">
            {{ $t('charge.label.total') }}
          </span>
          <span class="body-2">
            {{ formatMoney(totalToPay) }}
          </span>
        </p>
        <v-divider vertical />
        <v-btn @click="bulkPayment" color="primary" class="ml-5" elevation="0">
          <v-icon left>mdi-currency-usd</v-icon>
          <span class="body-2">
            {{ $t('charge.label.paySelected') }}
          </span>
        </v-btn>
      </div>
    </div>

    <h-pagination :fetch="fetchCharges" />

    <ChargeConfirmPayment
      :charge="charge"
      :charges="selectedCharges"
      :parsedCharges="parsedCharges"
      :totalToPay="totalToPay"
    />
  </div>
</template>

<script>
import ChargeListFilters from './ChargeListFilters'
import ChargeConfirmPayment from './ChargeConfirmPayment'

import { chargeTableHeaders } from '@/views/charge/listHeaders'
import { catalogDate, catalogMonetary } from '@/util/catalog'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { handleColors } from '@/util/colors'

export default {
  name: 'ChargeList',

  components: {
    ChargeListFilters,
    ChargeConfirmPayment,
  },

  computed: {
    ...mapGetters(['charges', 'isLoading', 'itemsPerPage', 'isGeneratingReport']),
  },

  watch: {
    selectedCharges() {
      this.handleTotalPayment()
    }
  },

  methods: {
    ...mapMutations(['openModal']),
    ...mapActions(['fetchCharges', 'fetchChargeReport']),

    handlePayment(charge) {
      this.charge = charge
      this.selectedCharges = []
      this.parsedCharges = []
      this.$nextTick(() => this.openModal('CONFIRM_CHARGE_PAYMENT_MODAL'))
    },

    handleChargeStatus(installments) {
      const status = installments.length && installments[0].status
      return status ? status : 'PENDING'
    },

    handleShowPayButton(status) {
      return status === 'PENDING'
    },

    handleTotalPayment() {
      if (!this.selectedCharges.length) {
        this.totalToPay = 0
        return
      }

      this.totalToPay = this.selectedCharges
        .map((item) => item.value)
        .reduce((prev, next) => prev + next)
    },

    bulkPayment() {
      this.charge = null
      this.parsedCharges = []
      this.selectedCharges.map((payment) => this.parsedCharges.push(payment.id))
      this.$nextTick(() => this.openModal('CONFIRM_CHARGE_PAYMENT_MODAL'))
    },

    resetSelected() {
      this.selectedCharges = []
    },

    toAppointmentDetails(id) {
      this.$router.push(`../appointments/${id}`).catch(() => {})
    },
  },

  data() {
    return {
      headers: chargeTableHeaders,
      formatMoney: catalogMonetary,
      formatDate: catalogDate,
      selectedCharges: [],
      parsedCharges: [],
      charge: null,
      handleColors,

      totalToPay: 0,
    }
  },

  mounted() {
    this.fetchCharges()
  },
}
</script>

<style>
.totalToPay {
  position: fixed;
  height: 60px;
  right: 160px;
  top: 147px;
  opacity: 0;
  transform: scale(0.9);
  transition: 700ms all cubic-bezier(.75,-0.5,0,1.75);
  box-shadow: 0px 6px 12px 0 rgb(226, 226, 226);
}

.totalToPay.show {
  opacity: 1;
  transform: scale(1);
}
</style>
